import { Edit, GitBranch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { GetAllCustomersDto } from '@/services/CustomerService';

interface CustomersTableProps {
  customers: Partial<GetAllCustomersDto>[];
  perPage: number;
}

export function CustomersTable({ customers, perPage }: CustomersTableProps) {
  const navigate = useNavigate();
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getPlatformBadge = (platform: string) => {
    const variant = platform === 'VTEX' ? 'default' : 'outline';
    return (
      <Badge
        variant={variant}
        className={
          platform === 'VTEX'
            ? 'bg-blue-100 text-blue-800 hover:bg-blue-100'
            : ''
        }
      >
        {platform}
      </Badge>
    );
  };

  return (
    <div className="overflow-x-auto bg-white px-2 shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">ID</TableHead>
            <TableHead className="w-1/4 font-semibold">Name</TableHead>
            <TableHead className="font-semibold">Platform</TableHead>
            <TableHead className="text-center font-semibold">
              Taxonomy Version
            </TableHead>
            <TableHead className="text-center font-semibold">
              Freemium
            </TableHead>
            <TableHead className="text-center font-semibold">
              Vision Last Submission
            </TableHead>
            <TableHead className="text-center font-semibold">
              Vision Auto Run
            </TableHead>
            <TableHead className="text-center font-semibold">Status</TableHead>
            <TableHead className="text-center font-semibold">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((customer) => (
            <TableRow
              key={customer.id}
              className="transition-colors hover:bg-gray-100"
            >
              <TableCell className="font-mono text-sm font-medium text-gray-600">
                {customer.id}
              </TableCell>

              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">
                    {customer.name}
                  </span>
                </div>
              </TableCell>

              <TableCell>{getPlatformBadge(customer.platform || '')}</TableCell>

              <TableCell className="text-center">
                <Badge variant="outline" className="font-mono hover:bg-white">
                  <GitBranch size={16} className="mr-1" /> v
                  {customer.taxonomyVersion || '1'}
                </Badge>
              </TableCell>

              <TableCell className="text-center">
                {customer.freemium ? 'Yes' : 'No'}
              </TableCell>

              <TableCell className="text-center">
                <span className="text-sm">
                  {formatDate(customer.visionLastSubmission || '')}
                </span>
              </TableCell>

              <TableCell className="text-center">
                <Badge
                  variant={customer.visionAutoRun ? 'default' : 'secondary'}
                  className="min-w-[70px] justify-center"
                >
                  {customer.visionAutoRun ? 'Enabled' : 'Disabled'}
                </Badge>
              </TableCell>

              <TableCell className="text-center">
                <Badge variant={customer.status ? 'default' : 'secondary'}>
                  {customer.status ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                <Button
                  variant="ghost"
                  className="h-8 w-8 hover:bg-white"
                  size="sm"
                  onClick={() =>
                    navigate(`/admin/customers/edit/${customer.id}`)
                  }
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {Array.from({ length: perPage - customers.length }).map(
            (_, index) => (
              <TableRow key={index} className="border-0 hover:bg-white">
                <TableCell colSpan={9} className="h-12" />
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </div>
  );
}
